import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { logger } from '../utils/logger';
import { baseAxiosConfig } from '../utils/apiConfig';

const billingAxios = axios.create(baseAxiosConfig);

/**
 * Create a Stripe Checkout session for a new subscription.
 * @param {string} successUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if user cancels
 * @returns {{ checkout_url: string }} The Stripe Checkout session URL
 */
export async function createCheckoutSession(successUrl, cancelUrl) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const response = await billingAxios.post(
      '/billing/create-checkout-session',
      { success_url: successUrl, cancel_url: cancelUrl },
      {
        params: { email: user.email },
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    logger.info('Checkout session created');
    return response.data;
  } catch (error) {
    logger.error('Error creating checkout session:', error);
    throw error;
  }
}

/**
 * Create a Stripe Checkout session for unauthenticated landing page users.
 * @param {string} email - User email from the landing form
 * @param {string} successUrl - URL to redirect after successful payment
 * @param {string} cancelUrl - URL to redirect if user cancels
 * @param {{ name?: string, niche?: string, isAgencyOwner?: boolean }} metadata - Optional metadata for user creation
 * @returns {{ checkout_url: string }} The Stripe Checkout session URL
 */
export async function createLandingCheckoutSession(email, successUrl, cancelUrl, { name, niche, isAgencyOwner } = {}) {
  try {
    const body = { success_url: successUrl, cancel_url: cancelUrl };
    if (name) body.name = name;
    if (niche) body.niche = niche;
    if (isAgencyOwner) body.is_agency_owner = true;

    const response = await billingAxios.post(
      '/billing/create-checkout-session',
      body,
      { params: { email } }
    );

    logger.info('Landing checkout session created');
    return response.data;
  } catch (error) {
    logger.error('Error creating landing checkout session:', error);
    throw error;
  }
}

/**
 * Create a Stripe Customer Portal session for billing management.
 * @returns {{ portal_url: string }} The Stripe Customer Portal URL
 */
export async function createPortalSession() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const response = await billingAxios.post(
      '/billing/create-portal-session',
      {},
      {
        params: { email: user.email },
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    logger.info('Portal session created');
    return response.data;
  } catch (error) {
    logger.error('Error creating portal session:', error);
    throw error;
  }
}

/**
 * Get the current subscription status for the authenticated user.
 * @returns {Object} Subscription status details
 */
export async function getSubscriptionStatus() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const response = await billingAxios.get('/billing/subscription-status', {
      params: { email: user.email },
      headers: { Authorization: `Bearer ${token}` },
    });

    logger.info('Subscription status fetched');
    return response.data;
  } catch (error) {
    logger.error('Error fetching subscription status:', error);
    throw error;
  }
}
